import { useEffect, useRef } from "react";
import { Link } from "react-router";

import { TokenMark } from "@/components/Brand";
import { ASQAmount, ConsumptionRail, Panel, Status } from "@/components/primitives";
import { formatASQ, formatDate, shortHash } from "@/lib/format";
import type { PaymentState } from "@/lib/use-payment";
import type { Utility } from "@/lib/api";

/**
 * The payment surface — a full-height bottom sheet on mobile, a centred panel
 * on desktop (plan §61, §14).
 *
 * It is one component across all three utilities rather than three, because the
 * thing it has to communicate is identical in every case and must not vary: how
 * much, to which vault, and that it cannot be undone. A per-utility sheet is
 * how one of them quietly loses the irreversibility notice.
 *
 * Three phases, and the sheet cannot be dismissed during the middle one — once
 * a wallet has been asked to sign, closing the surface that is tracking the
 * transaction would leave the user with a pending payment and nothing watching
 * it.
 */

export type PaymentSheetProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** What is being bought, in the user's words. */
  subject: string;
  /** Extra rows above the amount — duration, benefit, provider. */
  details?: { label: string; value: string }[];
  amountRaw: string;
  utility: Utility;
  state: PaymentState;
  onConfirm: () => void;
};

export function PaymentSheet({
  open,
  onClose,
  title,
  subject,
  details = [],
  amountRaw,
  utility,
  state,
  onConfirm,
}: PaymentSheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const inFlight =
    state.step === "preparing" ||
    state.step === "approving" ||
    state.step === "signing" ||
    state.step === "confirming" ||
    state.step === "verifying";

  const dismissable = !inFlight;

  // Focus moves into the dialog when it opens, and Escape closes it — but only
  // when there is nothing in flight. A focus trap that cannot be escaped and a
  // dialog that vanishes mid-signature are opposite failures of the same
  // control.
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissable) onClose();
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, dismissable, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-paper-50/55"
        onClick={dismissable ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-title"
        className="panel panel--marked relative flex max-h-[92dvh] w-full max-w-lg flex-col overflow-y-auto bg-ink-900 sm:max-h-[88dvh]"
      >
        <header className="flex items-start justify-between gap-4 border-b border-ink-700 p-6">
          <div>
            <p className="micro">{utility.name}</p>
            <h2 id="payment-title" className="display-section mt-2 text-2xl text-paper-50">
              {title}
            </h2>
            <p className="mt-1 text-sm text-paper-300/70">{subject}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="btn btn--ghost !min-h-9 !px-3 !text-[0.6875rem]"
            onClick={onClose}
            disabled={!dismissable}
          >
            {state.step === "done" ? "Tutup" : "Batal"}
          </button>
        </header>

        <div className="flex-1 p-6">
          {state.step === "done" && state.result ? (
            <SuccessBody state={state} utility={utility} />
          ) : (
            <ReviewBody
              details={details}
              amountRaw={amountRaw}
              utility={utility}
              state={state}
            />
          )}
        </div>

        <footer className="sticky bottom-0 border-t border-ink-700 bg-ink-900 p-6">
          {state.step === "done" ? (
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to="/activity" className="btn btn--ghost flex-1">
                Lihat Activity
              </Link>
              <Link to="/dashboard" className="btn btn--primary flex-1">
                Ke Dashboard
              </Link>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--primary w-full"
              onClick={onConfirm}
              disabled={inFlight || !utility.usable}
            >
              {inFlight ? "Memproses…" : state.step === "failed" ? "Coba Lagi" : "Lanjutkan"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function ReviewBody({
  details,
  amountRaw,
  utility,
  state,
}: {
  details: { label: string; value: string }[];
  amountRaw: string;
  utility: Utility;
  state: PaymentState;
}) {
  return (
    <div className="space-y-6">
      <dl className="space-y-3">
        {details.map((detail) => (
          <div key={detail.label} className="flex items-baseline justify-between gap-4">
            <dt className="micro">{detail.label}</dt>
            <dd className="text-sm text-paper-100">{detail.value}</dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-4 border-t border-ink-700 pt-3">
          <dt className="micro">Jumlah</dt>
          <dd className="flex items-center gap-2">
            <TokenMark size={18} />
            <ASQAmount raw={amountRaw} size="lg" />
          </dd>
        </div>
      </dl>

      {/*
        The rail. Where the money goes, drawn rather than described — and the
        one place the user sees the vault address before they sign.
      */}
      <Panel tone="flat" className="p-4">
        <ConsumptionRail
          from="Wallet Anda"
          to="Permanent Vault"
          amount={formatASQ(amountRaw)}
        />
        <p className="hexline mt-4 text-paper-300/60">{utility.vault}</p>
      </Panel>

      <Panel tone="paper" className="p-4">
        <p className="micro">Kunci permanen</p>
        <p className="mt-2 text-sm leading-relaxed text-ink-900">
          ASQ yang dibayarkan masuk ke vault utility ini dan{" "}
          <strong>tidak dapat ditarik kembali oleh siapa pun</strong>, termasuk
          oleh tim ASETQU. Tidak ada refund dan tidak ada pembatalan.
        </p>
      </Panel>

      {state.step !== "idle" ? <TransactionStepper state={state} /> : null}

      {state.error ? (
        <Panel tone="flat" className="border-danger/50 p-4">
          <Status state="bad">{state.error.code}</Status>
          <p className="mt-2 text-sm text-paper-300/80">{state.error.message}</p>
        </Panel>
      ) : null}
    </div>
  );
}

function SuccessBody({ state, utility }: { state: PaymentState; utility: Utility }) {
  const result = state.result!;

  return (
    <div className="space-y-6">
      <div>
        <Status state="ok">Terkonfirmasi</Status>
        <p className="mt-4">
          <ASQAmount raw={result.amountRaw} size="xl" />
        </p>
        <p className="mt-2 text-sm text-paper-300/70">terkunci permanen di vault utility ini.</p>
      </div>

      <ConsumptionRail
        from="Wallet Anda"
        to={utility.name}
        amount={formatASQ(result.amountRaw)}
      />

      {result.membership ? (
        <dl className="space-y-3 border-t border-ink-700 pt-5">
          <Row label="Paket" value={result.membership.planName} />
          <Row label="Aktif hingga" value={formatDate(result.membership.expiresAt)} />
        </dl>
      ) : null}

      {result.voucher ? (
        <div className="border-t border-ink-700 pt-5">
          <p className="micro">Kode voucher</p>
          <p className="datum mt-2 text-2xl text-paper-50">{result.voucher.code}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              className="btn btn--ghost !min-h-9 !text-[0.6875rem]"
              onClick={() => navigator.clipboard?.writeText(result.voucher!.code)}
            >
              Salin kode
            </button>
          </div>
          <dl className="mt-4 space-y-3">
            <Row label="Voucher" value={result.voucher.voucherName} />
            <Row label="Berlaku hingga" value={formatDate(result.voucher.expiresAt)} />
          </dl>
        </div>
      ) : null}

      {result.order ? (
        <dl className="space-y-3 border-t border-ink-700 pt-5">
          <Row label="Produk" value={result.order.productName} />
          <Row label="Status order" value={result.order.status} />
        </dl>
      ) : null}

      <dl className="space-y-3 border-t border-ink-700 pt-5">
        <Row label="Transaksi" value={shortHash(result.transactionHash)} />
      </dl>

      {result.explorerUrl ? (
        <a
          className="linkline text-sm text-hub-300"
          href={result.explorerUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          Lihat di block explorer
        </a>
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="micro">{label}</dt>
      <dd className="text-sm text-paper-100">{value}</dd>
    </div>
  );
}

/**
 * The stepper from plan §15.
 *
 * Numbered, and here the numbers earn it: this really is a sequence, the order
 * is what the user needs, and knowing which step failed is the difference
 * between "my wallet said no" and "the chain rejected it".
 *
 * Rendered as a vertical rail whose completed portion is filled — the same
 * language as the consumption rail, because it is the same journey.
 */
function TransactionStepper({ state }: { state: PaymentState }) {
  const steps: { key: PaymentState["step"]; label: string }[] = [
    { key: "preparing", label: "Menyiapkan pembayaran" },
    { key: "approving", label: "Approve ASQ" },
    { key: "signing", label: "Tanda tangan transaksi" },
    { key: "confirming", label: "Menunggu konfirmasi chain" },
    { key: "verifying", label: "Verifikasi event oleh server" },
  ];

  const order = steps.map((step) => step.key);
  const currentIndex = order.indexOf(state.step);
  const failed = state.step === "failed";

  return (
    <ol className="space-y-0 border-t border-ink-700 pt-5">
      {steps.map((step, index) => {
        const done = state.step === "done" || (currentIndex > index && currentIndex !== -1);
        const active = state.step === step.key;

        return (
          <li key={step.key} className="flex gap-4">
            <div className="flex flex-col items-center">
              <span
                className={`mt-1 h-2.5 w-2.5 shrink-0 ${
                  done
                    ? "bg-asq-emerald"
                    : active
                      ? "bg-warn"
                      : "border border-ink-600"
                }`}
                aria-hidden="true"
              />
              {index < steps.length - 1 ? (
                <span
                  className={`w-px flex-1 ${done ? "bg-asq-emerald" : "bg-ink-700"}`}
                  aria-hidden="true"
                />
              ) : null}
            </div>

            <div className="pb-4">
              <p className="micro">
                {String(index + 1).padStart(2, "0")}
              </p>
              <p
                className={`text-sm ${
                  active ? "text-paper-50" : done ? "text-paper-300/70" : "text-paper-300/40"
                }`}
              >
                {step.label}
              </p>
              {active && step.key === "confirming" ? (
                <p className="micro mt-1 text-warn">Menunggu blok</p>
              ) : null}
              {active && step.key === "verifying" && state.required > 0 ? (
                <p className="micro mt-1 text-warn">
                  Konfirmasi {state.confirmations} / {state.required}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}

      {failed ? (
        <li className="flex gap-4">
          <span className="mt-1 h-2.5 w-2.5 shrink-0 bg-danger" aria-hidden="true" />
          <p className="text-sm text-danger">Berhenti di langkah ini</p>
        </li>
      ) : null}
    </ol>
  );
}
